import React, { useMemo, useState } from 'react';
import './NewsPage.css';
import useNews from '../hooks/useNews';
import { NewsList, NewsFilters } from '../components/News';

const PAGE_SIZE = 8;
const DEFAULT_DAYS = 90;

// Local 'YYYY-MM-DD' for a Date (not toISOString(), which is UTC and can shift
// a day in western time zones — matching formatDate's local-date handling).
function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// 'YYYY-MM-DD' for `days` before today (today when days is 0/null).
function isoDaysAgo(days) {
  const d = new Date();
  if (days) d.setDate(d.getDate() - days);
  return toISO(d);
}

// Full list of community news with date filtering + client-side paging. Access
// (member/admin only) is enforced by ProtectedRoute; rendered inside the main
// shell. Reuses the shared NewsList (full articles) and NewsFilters so news
// renders and filters consistently with the rest of the site. Loads showing the
// last 90 days by default.
export default function NewsPage() {
  const { articles, status } = useNews({ max: 200 });
  const [from, setFrom] = useState(() => isoDaysAgo(DEFAULT_DAYS));
  const [to, setTo] = useState(() => isoDaysAgo(0));
  // Which quick-range preset (in days) the current from/to matches, if any —
  // drives the active chip. Starts on the 90-day default.
  const [presetDays, setPresetDays] = useState(DEFAULT_DAYS);
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () =>
      articles.filter((a) => {
        if (!a.date) return true;
        if (from && a.date < from) return false;
        if (to && a.date > to) return false;
        return true;
      }),
    [articles, from, to],
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pageCount);
  const shown = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const applyPreset = (days) => {
    setFrom(days == null ? '' : isoDaysAgo(days));
    setTo(isoDaysAgo(0));
    setPresetDays(days);
    setPage(1);
  };

  const changeFrom = (value) => {
    setFrom(value);
    setPresetDays(undefined);
    setPage(1);
  };

  const changeTo = (value) => {
    setTo(value);
    setPresetDays(undefined);
    setPage(1);
  };

  return (
    <div className="news-page">
      <div className="news-page-head">
        <h1>Community News</h1>
        <p className="news-page-lead">
          Updates and stories from the Dutchess County Bhabani community.
        </p>
      </div>

      {status === 'ready' && (
        <NewsFilters
          from={from}
          to={to}
          presetDays={presetDays}
          onFrom={changeFrom}
          onTo={changeTo}
          onPreset={applyPreset}
          count={filtered.length}
        />
      )}

      <NewsList
        articles={shown}
        status={status}
        emptyNote="No news in this date range — try widening the filter."
      />

      {pageCount > 1 && (
        <div className="news-pager">
          <button
            type="button"
            className="news-page-btn"
            disabled={current <= 1}
            onClick={() => setPage(current - 1)}
          >
            ← Prev
          </button>
          <span className="news-page-count">
            Page {current} of {pageCount}
          </span>
          <button
            type="button"
            className="news-page-btn"
            disabled={current >= pageCount}
            onClick={() => setPage(current + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
